import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  ChevronLeft,
  ChevronRight,
  Plus,
  Folder,
  FolderOpen,
  FileText,
  Trash2,
  Edit2,
  Save,
  Loader2,
  Settings2,
  MoreHorizontal,
  Clock,
  CheckCircle2,
  AlertCircle,
  Hourglass
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

const COLORS = [
  { value: '#3b82f6', name: 'Blue' },
  { value: '#10b981', name: 'Green' },
  { value: '#f59e0b', name: 'Amber' },
  { value: '#ef4444', name: 'Red' },
  { value: '#8b5cf6', name: 'Purple' },
  { value: '#ec4899', name: 'Pink' },
  { value: '#6366f1', name: 'Indigo' },
  { value: '#14b8a6', name: 'Teal' },
];

// Format duration in a human-readable way
const formatDuration = (seconds) => {
  if (!seconds) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    completed: { icon: CheckCircle2, class: 'text-green-600', label: 'completed' },
    processing: { icon: Hourglass, class: 'text-amber-600', label: 'processing' },
    failed: { icon: AlertCircle, class: 'text-red-600', label: 'failed' },
  };
  
  const { icon: Icon, class: colorClass, label } = config[status] || config.processing;
  
  return (
    <span className={`flex items-center gap-1 text-xs ${colorClass}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Lecture item component (like a file in Finder)
const LectureItem = ({ lecture }) => {
  return (
    <Link
      to={`/lecture/${lecture.id}`}
      className="flex items-center gap-3 py-2 px-3 ml-6 rounded-lg hover:bg-muted/50 transition-colors group"
      data-testid={`lecture-item-${lecture.id}`}
    >
      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-foreground truncate">
            {lecture.title}
          </span>
          {lecture.status === 'completed' && lecture.created_at && 
            new Date(lecture.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000) && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-600">
              New
            </span>
          )}
        </div>
      </div>

      {/* Metadata - always visible */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1 min-w-[80px]">
          <Clock className="w-3 h-3" />
          {formatDistanceToNow(new Date(lecture.created_at), { addSuffix: true })}
        </span>
        
        {lecture.audio_duration && (
          <span className="min-w-[50px] text-right">
            {formatDuration(lecture.audio_duration)}
          </span>
        )}
        
        <StatusBadge status={lecture.status} />
      </div>

      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
};

// Subject folder component (like a folder in Finder)
const SubjectFolder = ({ subject, lectures, onEdit, onDelete, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const subjectLectures = lectures
    .filter(l => l.subject_id === subject?.id)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  
  const folderColor = subject?.color || '#6b7280';
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group/folder">
      <div className="flex items-center">
        <CollapsibleTrigger className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors flex-1 text-left">
          <ChevronRight 
            className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} 
          />
          {isOpen ? (
            <FolderOpen className="w-5 h-5" style={{ color: folderColor }} />
          ) : (
            <Folder className="w-5 h-5" style={{ color: folderColor }} />
          )}
          <span className="font-medium text-foreground">{subject?.name || 'No Subject'}</span>
          {subject?.course_code && (
            <span className="text-xs text-muted-foreground">({subject.course_code})</span>
          )}
          <span className="text-xs text-muted-foreground ml-auto mr-2">
            {subjectLectures.length} lecture{subjectLectures.length !== 1 ? 's' : ''}
          </span>
        </CollapsibleTrigger>
        
        {subject && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 opacity-0 group-hover/folder:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(subject)}>
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Subject
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onDelete(subject)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Subject
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      
      <CollapsibleContent className="pl-4">
        {subjectLectures.length === 0 ? (
          <div className="py-3 px-3 ml-6 text-sm text-muted-foreground italic">
            No lectures in this {subject ? 'subject' : 'category'}
          </div>
        ) : (
          <div className="space-y-0.5">
            {subjectLectures.map(lecture => (
              <LectureItem key={lecture.id} lecture={lecture} />
            ))}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

// "No Subject" folder for uncategorized lectures
const UncategorizedFolder = ({ lectures }) => {
  const [isOpen, setIsOpen] = useState(true);
  const uncategorizedLectures = lectures
    .filter(l => !l.subject_id)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  
  if (uncategorizedLectures.length === 0) return null;
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors w-full text-left">
        <ChevronRight 
          className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} 
        />
        {isOpen ? (
          <FolderOpen className="w-5 h-5 text-gray-400" />
        ) : (
          <Folder className="w-5 h-5 text-gray-400" />
        )}
        <span className="font-medium text-foreground">Uncategorized</span>
        <span className="text-xs text-muted-foreground ml-auto">
          {uncategorizedLectures.length} lecture{uncategorizedLectures.length !== 1 ? 's' : ''}
        </span>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="pl-4">
        <div className="space-y-0.5">
          {uncategorizedLectures.map(lecture => (
            <LectureItem key={lecture.id} lecture={lecture} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function SubjectsPage() {
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [deleteSubject, setDeleteSubject] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    course_code: '',
    color: '#3b82f6',
    pct_multiple_choice: 40,
    pct_short_form: 30,
    pct_long_form: 30,
    pct_easy: 30,
    pct_medium: 50,
    pct_advanced: 20
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subjectsData, lecturesData] = await Promise.all([
        api.getSubjects(),
        api.getLectures()
      ]);
      setSubjects(subjectsData);
      setLectures(lecturesData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      course_code: '',
      color: '#3b82f6',
      pct_multiple_choice: 40,
      pct_short_form: 30,
      pct_long_form: 30,
      pct_easy: 30,
      pct_medium: 50,
      pct_advanced: 20
    });
  };

  const openEditModal = (subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name || '',
      description: subject.description || '',
      course_code: subject.course_code || '',
      color: subject.color || '#3b82f6',
      pct_multiple_choice: subject.pct_multiple_choice || 40,
      pct_short_form: subject.pct_short_form || 30,
      pct_long_form: subject.pct_long_form || 30,
      pct_easy: subject.pct_easy || 30,
      pct_medium: subject.pct_medium || 50,
      pct_advanced: subject.pct_advanced || 20
    });
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a subject name');
      return;
    }

    setSaving(true);
    try {
      const newSubject = await api.createSubject(formData);
      setSubjects([...subjects, newSubject]);
      setShowCreateModal(false);
      resetForm();
      toast.success('Subject created');
    } catch (error) {
      toast.error(error.message || 'Failed to create subject');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a subject name');
      return;
    }

    setSaving(true);
    try {
      const updated = await api.updateSubject(editingSubject.id, formData);
      setSubjects(subjects.map(s => s.id === updated.id ? updated : s));
      setEditingSubject(null);
      resetForm();
      toast.success('Subject updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update subject');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteSubject) return;

    try {
      await api.deleteSubject(deleteSubject.id);
      setSubjects(subjects.filter(s => s.id !== deleteSubject.id));
      setDeleteSubject(null);
      toast.success('Subject deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete subject');
    }
  };

  // Adjust percentages to sum to 100
  const adjustQuestionTypes = (field, value) => {
    const newData = { ...formData, [field]: value };
    const total = newData.pct_multiple_choice + newData.pct_short_form + newData.pct_long_form;
    
    if (total !== 100) {
      const diff = 100 - total;
      if (field === 'pct_multiple_choice') {
        newData.pct_short_form = Math.max(0, Math.min(100, newData.pct_short_form + Math.floor(diff / 2)));
        newData.pct_long_form = 100 - newData.pct_multiple_choice - newData.pct_short_form;
      } else if (field === 'pct_short_form') {
        newData.pct_long_form = Math.max(0, Math.min(100, newData.pct_long_form + diff));
      }
    }
    setFormData(newData);
  };

  const adjustDifficulty = (field, value) => {
    const newData = { ...formData, [field]: value };
    const total = newData.pct_easy + newData.pct_medium + newData.pct_advanced;
    
    if (total !== 100) {
      const diff = 100 - total;
      if (field === 'pct_easy') {
        newData.pct_medium = Math.max(0, Math.min(100, newData.pct_medium + Math.floor(diff / 2)));
        newData.pct_advanced = 100 - newData.pct_easy - newData.pct_medium;
      } else if (field === 'pct_medium') {
        newData.pct_advanced = Math.max(0, Math.min(100, newData.pct_advanced + diff));
      }
    }
    setFormData(newData);
  };

  // Sort subjects alphabetically
  const sortedSubjects = [...subjects].sort((a, b) => a.name.localeCompare(b.name));
  
  // Calculate total lectures
  const totalLectures = lectures.length;
  const uncategorizedCount = lectures.filter(l => !l.subject_id).length;

  const renderFormContent = (isEdit = false) => (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Subject Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Introduction to Psychology"
            className="mt-1.5"
            data-testid="subject-name-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="course_code">Course Code</Label>
            <Input
              id="course_code"
              value={formData.course_code}
              onChange={(e) => setFormData({ ...formData, course_code: e.target.value })}
              placeholder="e.g., PSY101"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {COLORS.map((color) => (
                <button
                  key={color.value}
                  onClick={() => setFormData({ ...formData, color: color.value })}
                  className={`w-8 h-8 rounded-full transition-transform ${
                    formData.color === color.value ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description..."
            className="mt-1.5"
          />
        </div>
      </div>

      {/* Question Preferences */}
      <div className="border-t pt-6">
        <h4 className="font-medium mb-4 flex items-center gap-2">
          <Settings2 className="w-4 h-4" />
          Question Generation Preferences
        </h4>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm text-muted-foreground">Question Types</Label>
            <div className="mt-2 space-y-3">
              <div className="flex items-center gap-4">
                <span className="w-32 text-sm">Multiple Choice</span>
                <Slider
                  value={[formData.pct_multiple_choice]}
                  onValueChange={([v]) => adjustQuestionTypes('pct_multiple_choice', v)}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{formData.pct_multiple_choice}%</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-32 text-sm">Short Form</span>
                <Slider
                  value={[formData.pct_short_form]}
                  onValueChange={([v]) => adjustQuestionTypes('pct_short_form', v)}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{formData.pct_short_form}%</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-32 text-sm">Long Form</span>
                <Slider
                  value={[formData.pct_long_form]}
                  onValueChange={([v]) => adjustQuestionTypes('pct_long_form', v)}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{formData.pct_long_form}%</span>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground">Difficulty Distribution</Label>
            <div className="mt-2 space-y-3">
              <div className="flex items-center gap-4">
                <span className="w-32 text-sm text-green-600">Easy</span>
                <Slider
                  value={[formData.pct_easy]}
                  onValueChange={([v]) => adjustDifficulty('pct_easy', v)}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{formData.pct_easy}%</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-32 text-sm text-amber-600">Medium</span>
                <Slider
                  value={[formData.pct_medium]}
                  onValueChange={([v]) => adjustDifficulty('pct_medium', v)}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{formData.pct_medium}%</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-32 text-sm text-red-600">Advanced</span>
                <Slider
                  value={[formData.pct_advanced]}
                  onValueChange={([v]) => adjustDifficulty('pct_advanced', v)}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="w-12 text-sm text-right">{formData.pct_advanced}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4">
        <Button
          variant="outline"
          onClick={() => {
            if (isEdit) setEditingSubject(null);
            else setShowCreateModal(false);
            resetForm();
          }}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          onClick={isEdit ? handleUpdate : handleCreate}
          disabled={saving}
          className="flex-1"
          data-testid="save-subject-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="mx-auto flex h-auto min-h-16 max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:h-16 sm:min-h-0 sm:flex-nowrap sm:px-6 sm:py-0">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <span className="font-serif text-xl text-foreground">Subjects & Lectures</span>
              <p className="text-xs text-muted-foreground">
                {totalLectures} lecture{totalLectures !== 1 ? 's' : ''} in {subjects.length} subject{subjects.length !== 1 ? 's' : ''}
                {uncategorizedCount > 0 && ` + ${uncategorizedCount} uncategorized`}
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="w-full rounded-xl sm:w-auto"
            data-testid="create-subject-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Subject
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : subjects.length === 0 && lectures.length === 0 ? (
          <div className="text-center py-20">
            <Folder className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-foreground mb-2">No subjects or lectures yet</h2>
            <p className="text-muted-foreground mb-6">
              Create subjects to organize your lectures and customize question generation.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <Button onClick={() => setShowCreateModal(true)} data-testid="first-subject-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create First Subject
              </Button>
              <Link to="/lecture">
                <Button variant="outline">
                  Record a Lecture
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <Card className="border-border/50 shadow-card">
            <CardContent className="p-2">
              {/* Finder-style file tree */}
              <div className="space-y-0.5">
                {/* Subjects */}
                {sortedSubjects.map((subject) => (
                  <SubjectFolder 
                    key={subject.id} 
                    subject={subject} 
                    lectures={lectures}
                    onEdit={openEditModal}
                    onDelete={setDeleteSubject}
                    defaultOpen={true}
                  />
                ))}
                
                {/* Uncategorized lectures at the bottom */}
                <UncategorizedFolder lectures={lectures} />
              </div>
              
              {/* Empty state within card if only has uncategorized */}
              {subjects.length === 0 && lectures.length > 0 && (
                <div className="border-t mt-4 pt-4 text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    Create subjects to organize your {lectures.length} lecture{lectures.length !== 1 ? 's' : ''}.
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowCreateModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Subject
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">Create New Subject</DialogTitle>
            <DialogDescription>
              Add a new subject to organize your lectures
            </DialogDescription>
          </DialogHeader>
          {renderFormContent(false)}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={!!editingSubject} onOpenChange={() => setEditingSubject(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">Edit Subject</DialogTitle>
            <DialogDescription>
              Update subject details and question preferences
            </DialogDescription>
          </DialogHeader>
          {renderFormContent(true)}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteSubject} onOpenChange={() => setDeleteSubject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subject?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove &quot;{deleteSubject?.name}&quot; but keep all associated lectures.
              They will move to &quot;Uncategorized&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
